export type IssuedVoucherMoney = {
    amount: number;
    currency: string;
};

export type IssuedVoucherLanguage = 'DE' | 'EN';

export type IssuedVoucherStatus = 'PAID' | 'REDEEMED' | 'REVOKED';

/**
 * Active and complete voucher template of the caller's lift operator that can
 * be used to issue vouchers.
 */
export type IssueVoucherTemplateDTO = {
    id: string;
    name: string;
    language: IssuedVoucherLanguage;
    defaultText: string;
};

export type IssueVouchersCommand = {
    voucherTemplateId: string;
    amount: { amount: number; currency: 'EUR' };
    /** 1..10, defaults to 1 on the server. */
    quantity?: number;
    /** YYYY-MM-DD, must not be in the past; null means unlimited. */
    validUntil?: string | null;
    sender?: string;
    receiver?: string;
    /** Empty text falls back to the default text of the template. */
    text?: string;
    /** Internal reason, required. */
    note: string;
};

export type IssuedVoucherDTO = {
    id: string;
    code: string;
    initialAmount: IssuedVoucherMoney;
    redeemableAmount: IssuedVoucherMoney;
    status: IssuedVoucherStatus;
    validUntil: string | null;
    /** ISO local date time. */
    issuedAt: string;
    /** User id of the issuer (lift operator id for the main account). */
    issuedBy: string;
    voucherTemplateId: string;
    title: string;
    language: IssuedVoucherLanguage;
    sender: string;
    receiver: string;
    text: string;
    note: string;
};

export type IssueVouchersResponseDTO = {
    vouchers: IssuedVoucherDTO[];
};

/**
 * Wire format of the order service: Jackson writes LocalDate and LocalDateTime
 * as number arrays ([2029, 1, 1] / [2026, 10, 16, 8, 48, 30, 601239000]).
 */
export type IssuedVoucherBackendDTO = Omit<
    IssuedVoucherDTO,
    'validUntil' | 'issuedAt'
> & {
    validUntil: number[] | string | null;
    issuedAt: number[] | string;
};

const pad = (value: number) => String(value).padStart(2, '0');

/** LocalDate → YYYY-MM-DD, without going through the time zone. */
const toIsoDate = (value: number[] | string | null): string | null => {
    if (value == null) return null;
    if (typeof value === 'string') return value;
    const [year, month, day] = value;
    if (!year || !month || !day) return null;
    return `${year}-${pad(month)}-${pad(day)}`;
};

/** LocalDateTime (server time, UTC) → ISO string. */
const toIsoDateTime = (value: number[] | string): string => {
    if (typeof value === 'string') return value;
    const [year, month, day, hours = 0, minutes = 0, seconds = 0] = value;
    if (!year || !month || !day) return '';
    return new Date(
        Date.UTC(year, month - 1, day, hours, minutes, seconds)
    ).toISOString();
};

export const convertIssuedVoucherToFrontend = (
    voucher: IssuedVoucherBackendDTO
): IssuedVoucherDTO => ({
    ...voucher,
    validUntil: toIsoDate(voucher.validUntil),
    issuedAt: toIsoDateTime(voucher.issuedAt),
});

export type GetIssuedVouchersParams = {
    code?: string;
    page: number;
    size: number;
};

export type GetIssuedVouchersResponse = {
    content: IssuedVoucherDTO[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
};

export const ISSUED_VOUCHER_STATUS_LABELS: Record<IssuedVoucherStatus, string> =
    {
        PAID: 'Aktiv',
        REDEEMED: 'Eingelöst',
        REVOKED: 'Widerrufen',
    };

/** Server-side limits, mirrored for client-side validation. */
export const ISSUE_VOUCHER_LIMITS = {
    minAmount: 5,
    maxAmount: 1000,
    minQuantity: 1,
    maxQuantity: 10,
    maxNameLength: 32,
    maxTextLength: 500,
    maxNoteLength: 500,
} as const;
